import { useMemo, useState } from "react";

import Editor from "@/design/components/Editor";
import EditorList from "@/design/components/EditorList";
import EditorSidebar from "@/design/components/EditorSidebar";
import InputPhone from "@/design/forms/InputPhone";
import InputSelect from "@/design/forms/InputSelect";
import Valkyrie, { viCheck, viCircleInfo, viLock, viPlus, viXmark } from "@sippy-platform/valkyrie";

import { Box, Button, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import PropTypes from "prop-types";

import RuleButton from "./RuleButton";
import RuleForm from "./RuleForm";

function RuleEditor({ blocklist, blocklistDispatch }) {
  const isPbxBlocklist = useMemo(() => blocklist.type === "pbx", [blocklist.type]);

  const [activeRule, setActiveRule] = useState(null);

  // Tester
  const [testPrefix, setTestPrefix] = useState("+");
  const [testDirection, setTestDirection] = useState("both");

  function getPrefixMatchScore(prefix) {
    let score = prefix.length;

    // Detract X * n / 10
    score = score - (prefix.match(/X/g)?.length ?? 0) / 10;

    for (let index = 0; index < prefix.length; index++) {
      if (prefix[index] === "X") {
        /*
         * Score the position of each X by the length minus its position divided
         * by the length, divided by 10. If the position minus the length calculates
         * to 0, we take a sixteenth instead. The maximum length of a prefix is 15
         * therefor 1/16 is the next lowest notation for a score.
         */
        score = score - ((prefix.length - (index + 1)) / prefix.length || 1 / 16) / 10;
      }
    }

    return score;
  }

  const [rules, testResult] = useMemo(() => {
    if (testPrefix === "" || testPrefix === "+") {
      return [blocklist.rules, undefined];
    }

    const applicatedRules = [];

    const blocklistBlocksIn = !blocklist.allowExternalIn;
    const blocklistBlocksOut = !blocklist.allowExternalOut;

    const direction = isPbxBlocklist ? testDirection : "out";

    blocklist.rules.map((rule) => {
      // Replace X with regex range for 0-9, add $ if X is last character in prefix
      const endsWithWildcard = rule.prefix.charAt(rule.prefix.length - 1) === "X";
      const regexRule = rule.prefix.replaceAll("X", "[0-9]").replace("+", "\\+");
      const regex = new RegExp(`^${regexRule}${endsWithWildcard ? "$" : ""}`);

      if (regex.test(testPrefix)) {
        const score = getPrefixMatchScore(rule.prefix);

        // List all rules that apply
        if (
          direction === "both" ||
          (direction === "in" && rule.direction !== "out") ||
          (direction === "out" && rule.direction !== "in")
        ) {
          if (
            (rule.direction === "in" && (!blocklistBlocksIn || rule.allowed)) ||
            (rule.direction === "out" && (!blocklistBlocksOut || rule.allowed))
          ) {
            applicatedRules.push({ ...rule, score });
          }
        }
      }
    });

    // Special rules for blocklist settings and emergency services
    if (!blocklist.allowExternalIn && direction !== "out") {
      applicatedRules.push({
        type: "All external outgoing calls are blocked.",
        direction: "in",
        allowed: false,
        score: 0,
      });
    }

    if (!blocklist.allowExternalOut && direction !== "in") {
      applicatedRules.push({
        type: "All external incoming calls are blocked.",
        direction: "out",
        allowed: false,
        score: 0,
      });
    }

    if (direction !== "in" && Number(testPrefix) >= 100 && Number(testPrefix) <= 119 && !testPrefix.startsWith("+")) {
      applicatedRules.push({
        type: "This number is an emergency service.",
        direction: "out",
        allowed: true,
        score: 17, // 15 characters + "+" + 1
      });
    }

    // Sort rules and determine if the result will allow or not allow the call to continue
    applicatedRules.sort((a, b) => (a.score > b.score ? -1 : 1));
    const inRules = applicatedRules.filter((rule) => rule.direction !== "out");
    const outRules = applicatedRules.filter((rule) => rule.direction !== "in");

    const isInAllowed = direction !== "out" ? inRules?.length === 0 || inRules[0].allowed : undefined;
    const isOutAllowed = direction !== "in" ? outRules?.length === 0 || outRules[0].allowed : undefined;

    return [
      applicatedRules,
      {
        in: isInAllowed,
        out: isOutAllowed,
        severity:
          isInAllowed === undefined
            ? isOutAllowed
              ? "success"
              : "danger"
            : isOutAllowed === undefined
              ? isInAllowed
                ? "success"
                : "danger"
              : isInAllowed && isOutAllowed
                ? "success"
                : isInAllowed || isOutAllowed
                  ? "warning"
                  : "danger",
      },
    ];
  }, [
    isPbxBlocklist,
    blocklist.allowExternalIn,
    blocklist.allowExternalOut,
    blocklist.rules,
    testDirection,
    testPrefix,
  ]);

  return (
    <Editor
      title="Rules"
      id="ruleEditorCard"
      controls={
        <>
          <Stack
            direction="row"
            sx={{
              gap: 2,
              mt: 2,
            }}
          >
            {isPbxBlocklist && (
              <InputSelect
                label="Direction"
                onChange={(e, value) => setTestDirection(value)}
                value={testDirection}
                selectValue="value"
                selects={[
                  { value: "in", title: "Incoming" },
                  { value: "out", title: "Outgoing" },
                  { value: "both", title: "Both" },
                ]}
                getOptionLabel={(option) => option.title}
                sx={{ maxWidth: 200 }}
              />
            )}
            <InputPhone
              id="testPrefix"
              label="Type to test a number"
              onChange={(_, value) => setTestPrefix(value)}
              value={testPrefix || ""}
            />
          </Stack>
          <>
            {testResult && (
              <Stack
                direction="row"
                sx={{
                  gap: 2,
                  mt: 1.5,
                }}
              >
                {testResult.in !== undefined && (
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.5,
                      alignItems: "center",
                      color: testResult.in ? "success.main" : "error.main",
                    }}
                  >
                    <Valkyrie icon={testResult.in ? viCheck : viXmark} />
                    <Typography>{testResult.in ? "Incoming calls allowed" : "Incoming calls blocked"}</Typography>
                  </Stack>
                )}
                {testResult.out !== undefined && (
                  <Stack
                    direction="row"
                    sx={{
                      gap: 0.5,
                      alignItems: "center",
                      color: testResult.out ? "success.main" : "error.main",
                    }}
                  >
                    <Valkyrie icon={testResult.out ? viCheck : viXmark} />
                    <Typography>{testResult.out ? "Outgoing calls allowed" : "Outgoing calls blocked"}</Typography>
                  </Stack>
                )}
              </Stack>
            )}
          </>
        </>
      }
      actions={
        <Button
          variant="contained"
          startIcon={<Valkyrie icon={viPlus} />}
          onClick={() => setActiveRule({ prefix: "+", direction: "out", description: "", allowed: false })}
        >
          Add
        </Button>
      }
    >
      <Box>
        <EditorList columns={1} direction="row">
          {rules.length === 0 && (!testPrefix || testPrefix === "+") && (
            <ListItem>
              <ListItemIcon>
                <Valkyrie icon={viCircleInfo} />
              </ListItemIcon>
              <ListItemText primary="There are no rules in the blocklist." />
            </ListItem>
          )}
          {rules.length === 0 &&
            testPrefix &&
            testPrefix !== "+" &&
            (blocklist.allowExternalIn || blocklist.allowExternalIn === null) &&
            blocklist.allowExternalOut && (
              <ListItem>
                <ListItemIcon>
                  <Valkyrie icon={viCircleInfo} />
                </ListItemIcon>
                <ListItemText primary="There are no rules that apply to this prefix." />
              </ListItem>
            )}
          {rules?.map((rule, key) => {
            if (rule.type) {
              return (
                <ListItem key={key}>
                  <ListItemIcon
                    sx={{
                      color: rule.allowed ? "success.main" : "error.main",
                    }}
                  >
                    <Valkyrie icon={viLock} />
                  </ListItemIcon>
                  <ListItemText primary={rule.type} />
                </ListItem>
              );
            } else {
              return (
                <RuleButton
                  key={key}
                  blocklist={blocklist}
                  rule={rule}
                  setActiveRule={setActiveRule}
                  blocklistDispatch={blocklistDispatch}
                  activeRule={activeRule}
                />
              );
            }
          })}
        </EditorList>
      </Box>
      <EditorSidebar showForm={!!activeRule}>
        <RuleForm
          key={activeRule?.id}
          blocklist={blocklist}
          activeRule={activeRule}
          blocklistDispatch={blocklistDispatch}
          setActiveRule={setActiveRule}
        />
      </EditorSidebar>
    </Editor>
  );
}

RuleEditor.propTypes = {
  blocklist: PropTypes.object,
  blocklistDispatch: PropTypes.func,
};

export default RuleEditor;
